/**
 * Shared search/share metadata helpers. Pure constants and functions, so both
 * server metadata exports and the client lang sync can import them.
 */

/**
 * The share card every marketing page uses. Every colocated
 * `opengraph-image.tsx` in the app re-exports the same render
 * (src/app/opengraph-image.tsx), so pointing at the root route is the same
 * picture at one cacheable URL.
 *
 * WHY IT HAS TO BE PASSED EXPLICITLY: Next 14 attaches a folder's file-based
 * image to the segment one level BELOW that folder, after that segment's own
 * metadata (next-app-loader hands a directory's static metadata to its child
 * node). A page or layout that declares `openGraph` without `images` therefore
 * replaces the inherited object and silently drops the picture — no warning,
 * no build error. That is how ~889 pages (every buurt guide, /pricing,
 * /preview, /legal, /founder) shipped with no og:image while the homepage had
 * one. So: a metadata export that sets `openGraph` either passes
 * `images: [DEFAULT_OG_IMAGE]` or has its own opengraph-image.tsx beside it.
 * `npm run test:seo` checks the built pages for it.
 */
export const DEFAULT_OG_IMAGE = {
  url: "/opengraph-image",
  width: 1200,
  height: 630,
  alt: "PlotScore — One score for every Dutch property",
  type: "image/png",
};

export type SiteLocale = "en" | "nl";

/** Every Dutch page lives under /nl — that prefix is the whole rule. */
export function localeForPath(pathname: string): SiteLocale {
  return pathname === "/nl" || pathname.startsWith("/nl/") ? "nl" : "en";
}

/**
 * The root layout can print only one `<html lang>` for every route, and it
 * prints "en" — a single root layout cannot know the URL without opting all
 * ~920 static pages out of static rendering. So the Dutch half is corrected
 * three ways: this blocking script sets `lang="nl"` before first paint (screen
 * readers and JS-rendering crawlers), `HtmlLangSync` keeps it right across
 * client-side navigation, and next.config.js sends `Content-Language: nl` on
 * /nl for crawlers that never run a script (Bing reads the header). Same rule
 * as `localeForPath`, inlined because it runs before any module loads.
 */
export const LANG_BOOT_SCRIPT = `(function(){try{var p=location.pathname;if(p==="/nl"||p.indexOf("/nl/")===0)document.documentElement.lang="nl";}catch(e){}})();`;
